import FlapUtil from './flap-util';
import SectionUtil from './section-util';

/* TODO: there are a bunch of projection/search
 * results in here that maybe don't make sense anymore and/or may not be used.
 */

/**
 * Flap's category for an RSS feed matched by URL. Named for the grab-bag of internal debug
 * searches it shares (flap's `EasterEggs`), of which the feed lookup is the only public one.
 */
export const FEED_RESULT_CATEGORY = 'eastereggs';

const RSS_FEED_TYPE = 'rss';

const SECTION_RESULT_CATEGORIES = [
  'top_result',
  'topic',
  'magazine',
  'profile',
  'source',
  FEED_RESULT_CATEGORY,
];

/**
 * Returns a projection of a topic with useful properties used by components
 * @param  {Object} topic - Search topic result
 * @return {Object} - Projection of the search result topic category
 */
function topicProjection(topic) {
  return Object.assign({}, topic, {
    topicTag: FlapUtil.getTopicTagFromRemoteid(topic.remoteid),
  });
}

/**
 * Returns a projection of a search result with useful properties used by components
 * @param  {Object} result - Search result (contains searchResultItems)
 * @return {Object} - Projection of the search result item
 */
function searchResultProjection(result) {
  const { category } = result;

  let projected = Object.assign({}, result);

  // Decorate topic results
  if (category === 'topic') {
    projected = Object.assign({}, projected, {
      searchResultItems: result.searchResultItems.map((topic) =>
        topicProjection(topic),
      ),
    });
  }

  // Do section projections on all section results
  if (SECTION_RESULT_CATEGORIES.includes(category)) {
    projected = Object.assign({}, result, {
      searchResultItems: result.searchResultItems.map((section) =>
        SectionUtil.projection(section),
      ),
    });
  }

  return projected;
}

function topicsProjection(stream = []) {
  return (
    stream
      .find((i) => i.category === 'topic')
      ?.searchResultItems?.map((i) => SectionUtil.projection(i))
      .filter(SectionUtil.isTopic) || []
  );
}

/**
 * Strips the favicon flap attaches to a feed search result. It is a 16px http:// URL, which a
 * tile renders as a blocked, upscaled cover; with no image the tile fetches the section's real
 * cover instead.
 * @param {Object} feed - Flap feed search result
 * @return {Object} - The result without its favicon
 */
function withoutFavicon(feed) {
  const section = Object.assign({}, feed);
  delete section.imageURL;
  delete section.isFavicon;
  return section;
}

/**
 * Collapses RSS feed hits into a single result bucket.
 *
 * Flap returns a feed matched by URL as a flat stream entry rather than the `searchResultItems`
 * bucket every other category uses, so without this the rest of the search machinery cannot see
 * it. Entries that are not feed sections are dropped: the category also carries flap's internal
 * debug searches, which have never been reachable from the web.
 *
 * @param  {Array} searchResults Flipboard search results array
 * @return {Array} - Search results with feed entries replaced by one bucketed result
 */
function collapseFeedResults(searchResults) {
  const feeds = searchResults.filter(
    ({ category, feedType }) =>
      category === FEED_RESULT_CATEGORY && feedType === RSS_FEED_TYPE,
  );
  const rest = searchResults.filter(
    ({ category }) => category !== FEED_RESULT_CATEGORY,
  );
  if (feeds.length === 0) {
    return rest;
  }
  return [
    {
      category: FEED_RESULT_CATEGORY,
      categoryTitle: feeds[0].categoryTitle,
      searchResultItems: feeds.map(withoutFavicon),
    },
    ...rest,
  ];
}

/**
 * Returns a projection of search results with useful properties used by components
 * @param  {Array} searchResults Flipboard search results array
 * @return {Object} - Projected Flipboard search results
 */
function projection(searchResults) {
  return collapseFeedResults(searchResults).map((result) =>
    searchResultProjection(result),
  );
}

/**
 * Returns the search result items for a specified result category
 * @param  {Array} searchResults Flipboard search results array
 * @param {String} category  The name of the result category to return
 * @return {Object} - Projected Flipboard search results
 */
function resultItems(searchResults, category) {
  const result =
    searchResults &&
    searchResults.find((result) => result.category === category);
  return (result && result.searchResultItems) || [];
}

export default {
  projection,
  topicsProjection,
  resultItems,
};
